import {
  FunctionComponent,
  useState,
  useMemo,
  useEffect,
  useCallback,
} from 'react'
import { Breadcrumb } from '@/components/RebrandedBreadcrumb'
import { useLocation, useHistory, Redirect } from 'react-router-dom'
import {
  PreCallTest as PreCallTestComponent,
  PreCallResults,
  useConnectionStore,
  QualityValues,
} from '@conexasaude/conexa-pre-call-test'

import CustomLayout from '@/components/RebrandedCustomLayout'
import { useZendeskStore } from '@/stores/useZendeskStore'
import { RenderIf } from '@/components/RenderIf'
import { DeviceSuccessModal } from './modals/DeviceSuccessModal'
import { DeviceWarningModal } from './modals/DeviceWarningModal'
import { DeviceErrorModal } from './modals/DeviceErrorModal'
import { useModalContext } from '@/components/RebrandedModal/contexts/ModalContext'
import { useConsultationDetails } from './hooks/useConsultationDetails'
import { useMedicalSpecialitiesContext } from '@/modules/MedicalSpecialities/contexts/MedicalSpecialitiesContext'
import { preCallTestBreadcrumbList } from './consts'
import { UseLocationState, ConsultationType } from './types'
import { logPreCallTest } from '../../video-call-services'
import { openWhatsappSupport } from '@/utils/openWhatsappSupport'
import { toast, Banner, Alert } from '@conexasaude/conexa-hero'
import { HeroWrapper } from '@/styles/rebranded/HeroWrapper'
import useStyles from './styles'

const modals = {
  [QualityValues.good]: {
    modalId: 'DEVICE_SUCCESS',
    title: 'Teste realizado',
  },
  [QualityValues.medium]: {
    modalId: 'DEVICE_WARNING',
    title: 'Dispositivo testado',
  },
  [QualityValues.bad]: {
    modalId: 'DEVICE_ERROR',
    title: 'Teste realizado',
  },
}

const PreCallTest: FunctionComponent = () => {
  const [preCallTestResults, setPreCallTestResults] =
    useState<PreCallResults>(null)
  const [hasAccessRejected, setHasAccessRejected] = useState<boolean>(false)
  const [isWithoutConnection, setIsWithoutConnection] = useState<boolean>(false)
  const [hasInstableConnection, setHasInstableConnection] =
    useState<boolean>(false)
  const location = useLocation<UseLocationState>()
  const history = useHistory()
  const { isLoading, testConnection } = useConnectionStore()
  const setIsVisible = useZendeskStore(state => state.setIsVisible)
  const { showModal } = useModalContext()
  const { consultationDetails } = useConsultationDetails(
    location?.state?.consultationId,
  )
  const { specialities } = useMedicalSpecialitiesContext()
  const styles = useStyles()
  const isScheduled = useMemo(() => {
    return location?.state?.consultationType === ConsultationType.SCHEDULED
  }, [location?.state?.consultationType])

  const isAllowedToContinue = useMemo(() => {
    if (isLoading) return false

    if (isScheduled) {
      return !preCallTestResults?.isOffline
    }

    if (
      preCallTestResults?.accessAllowed !== 'accepted' ||
      preCallTestResults?.networkResults === QualityValues.bad
    ) {
      return false
    }

    return true
  }, [
    isLoading,
    isScheduled,
    preCallTestResults?.accessAllowed,
    preCallTestResults?.networkResults,
    preCallTestResults?.isOffline,
  ])

  const isScheduledAttendance = useMemo(() => {
    return location?.state?.consultationType === ConsultationType.SCHEDULED
  }, [location?.state?.consultationType])

  const hasFromOrToStates = useMemo(() => {
    return location?.state?.from || location?.state?.to
  }, [location?.state?.from, location?.state?.to])

  const medicSpeciality = useMemo(() => {
    const speciality = specialities?.find(speciality => {
      return speciality?.id === consultationDetails?.professional?.type
    })

    return speciality
  }, [consultationDetails?.professional, specialities])

  useEffect(() => {
    setIsVisible(false)

    return () => {
      setIsVisible(true)
    }
  }, [setIsVisible])

  const handleAccessRejectedAction = useCallback(() => {
    setHasAccessRejected(false)
  }, [])

  const handleWithoutConnectionAction = useCallback(() => {
    if (preCallTestResults?.isOffline) {
      toast({
        message:
          'Você não está conectado, verifique o sinal de internet e tente novamente.',
        variant: 'error',
      })
      return
    }
    testConnection()
    setIsWithoutConnection(false)
  }, [preCallTestResults?.isOffline, testConnection])

  const handleWithoutConnectionClose = useCallback(() => {
    setIsWithoutConnection(false)
  }, [])

  const handleHasInstableConnectionAction = useCallback(() => {
    if (preCallTestResults?.isOffline) {
      toast({
        message:
          'Você não está conectado, verifique o sinal de internet e tente novamente.',
        variant: 'error',
      })

      return
    }

    testConnection()
    setHasInstableConnection(false)
  }, [preCallTestResults?.isOffline, testConnection])

  const handleHasInstableConnectionClose = useCallback(
    () => setHasInstableConnection(false),
    [],
  )

  const handlePreCallTestSuccess = useCallback(() => {
    if (!isScheduledAttendance) {
      history.push(location?.state?.to, {
        from: '/teste-de-chamada',
        ...location?.state?.forwardState,
      })

      return
    }

    let modalId = modals[preCallTestResults?.networkResults]?.modalId
    let modalTitle = modals[preCallTestResults?.networkResults]?.title

    const isWithourPermissions =
      preCallTestResults?.accessAllowed !== 'accepted'

    if (isWithourPermissions) {
      modalId = modals.BAD.modalId
      modalTitle = modals.BAD.title
    }

    showModal(modalId, {
      title: modalTitle,
    })
  }, [
    history,
    isScheduledAttendance,
    location?.state?.forwardState,
    location?.state?.to,
    preCallTestResults?.accessAllowed,
    preCallTestResults?.networkResults,
    showModal,
  ])

  const handlePreCallTestFail = useCallback(() => {
    if (preCallTestResults?.isOffline) {
      toast({
        message: 'Sem conexão. Por favor, verifique o sinal de internet.',
        variant: 'error',
      })
      return
    }
    openWhatsappSupport()
  }, [preCallTestResults?.isOffline])

  const handlePreCallTestResults = useCallback(
    async (results: PreCallResults) => {
      setPreCallTestResults(results)
      setHasAccessRejected(results.accessAllowed === 'rejected')
      setIsWithoutConnection(
        results.isOffline || results?.networkResults === QualityValues.bad,
      )
      setHasInstableConnection(results?.networkResults === QualityValues.medium)

      logPreCallTest({
        idAtendimento: location?.state?.consultationId || null,
        accessAllowed: results?.accessAllowed,
        mediaCreated: results?.mediaCreated,
        networkResults: results?.networkResults,
        plataforma: 'WEB',
        browserName: results?.browserName,
        mobileModel: results?.mobileModel,
        mobileVendor: results?.mobileVendor,
        osName: results?.osName,
      })
    },
    [location?.state?.consultationId],
  )

  // Quando fazemos push para o /teste-de-chamada, é preciso passar os states "from" e "to"
  // caso o atendimento seja imediato para que possamos identificar de onde veio o push e
  // conseguir redirecionar quando o teste passar.
  // Ex.: history.push('/teste-de-chamada', { from: '/', to: '/triagem' })
  if (!isScheduledAttendance && !hasFromOrToStates) {
    return <Redirect to="/" />
  }

  return (
    <HeroWrapper>
      <div css={styles.container}>
        <RenderIf condition={isWithoutConnection}>
          <div css={styles.bannerContainer}>
            <Banner
              variant="error"
              actionLabel="Fazer novo teste"
              text="Sem conexão. Por favor, verifique o sinal de internet e faça um novo teste."
              action={handleWithoutConnectionAction}
              actionIsLoading={isLoading}
              onClose={handleWithoutConnectionClose}
            />
          </div>
        </RenderIf>

        <RenderIf condition={hasInstableConnection}>
          <div css={styles.bannerContainer}>
            <Banner
              variant="warning"
              actionLabel="Fazer novo teste"
              text="Conexão instável. Por favor, verifique o sinal de internet e faça um novo teste."
              action={handleHasInstableConnectionAction}
              actionIsLoading={isLoading}
              onClose={handleHasInstableConnectionClose}
            />
          </div>
        </RenderIf>

        <RenderIf condition={hasAccessRejected}>
          <div css={styles.alertContainer}>
            <Alert
              variant="error"
              actionLabel="Ok, entendi"
              title="Câmera e microfone não estão habilitados."
              text="Para habilitar, acesse as Configurações dos dispositivos ou verifique as configurações no seu computador. Se precisar de ajuda, converse com nosso Suporte."
              action={handleAccessRejectedAction}
              actionIsLoading={isLoading}
            />
          </div>
        </RenderIf>
      </div>

      <CustomLayout
        hasLayoutMenu
        layoutMenu={<Breadcrumb items={preCallTestBreadcrumbList} />}
        hasHeader
        title="Teste de chamada"
        caption="Antes da consulta, precisamos verificar a câmera, o microfone e a conexão."
        hasPrimaryAction
        primaryAction={handlePreCallTestSuccess}
        primaryActionLabel="Continuar"
        primaryActionProps={{
          disabled: !isAllowedToContinue,
        }}
        hasNegativeAction
        negativeAction={handlePreCallTestFail}
        negativeActionLabel="Conversar com Suporte"
      >
        {/* Remoção da prop OTNetworkTestConfig */}
        {/* pois estava causando problema em produção */}
        <PreCallTestComponent
          getResults={handlePreCallTestResults}
          audioTestURL={process.env.REACT_APP_PRECALLTEST_AUDIO_URL}
          // OTNetworkTestConfig={otNetworkConfig}
        />
      </CustomLayout>

      <DeviceSuccessModal consultationId={location?.state?.consultationId} />
      <DeviceWarningModal consultationId={location?.state?.consultationId} />
      <DeviceErrorModal
        consultationId={location?.state?.consultationId}
        professionalType={consultationDetails?.professional?.type}
        professionalId={consultationDetails?.professional?.id}
        tokenId={consultationDetails?.tokenId}
        canReschedule={medicSpeciality?.canSchedule}
      />
    </HeroWrapper>
  )
}

export { PreCallTest }
